import { useEffect, useLayoutEffect, useRef, type RefObject } from "react";
import type { CartItem } from "../state/cart";
import { useHoldKeepAlive } from "../state/use-hold-keepalive";

/**
 * The checkout's hold timer: the time left, shown only in the last five
 * minutes, "We can hold this for N more minutes" when no more time is coming,
 * and the lapse message. It keeps the hold alive while the guest is typing
 * (the rules are in state/hold-timer-logic.ts).
 *
 * Two direct children of the checkout form, never a wrapper: the banner has
 * to be the form's own child to stay stuck to the top of the drawer while the
 * form scrolls, and the live region is absolutely positioned so it adds no
 * gap to the form's column. The live region says one sentence a minute; the
 * banner's m:ss changes every second and is hidden from screen readers so it
 * is never read out second by second.
 *
 * While the banner shows, the form's scroll-padding-top is the banner's
 * height (--vt-hold-timer-h, kept current by a ResizeObserver), so a field
 * the guest tabs to is scrolled clear of the banner, never under it (WCAG
 * 2.4.11).
 *
 * Once the hold has ended, `onEndedChange(true)` lets CheckoutView disable
 * Pay, and the banner offers "Back to your visit" (`onBack`).
 */
export function HoldTimer({
  items,
  formRef,
  onBack,
  onEndedChange,
}: {
  items: readonly CartItem[];
  formRef: RefObject<HTMLElement | null>;
  onBack: () => void;
  onEndedChange: (ended: boolean) => void;
}) {
  const view = useHoldKeepAlive(items, formRef);
  const shown = view.kind !== "hidden";
  const ended = view.kind === "ended";

  const endedRef = useRef(onEndedChange);
  endedRef.current = onEndedChange;
  useEffect(() => {
    endedRef.current(ended);
  }, [ended]);

  const bannerRef = useRef<HTMLDivElement | null>(null);
  useLayoutEffect(() => {
    const form = formRef.current;
    const banner = bannerRef.current;
    if (!shown || !form || !banner) return;
    const measure = () =>
      form.style.setProperty("--vt-hold-timer-h", `${Math.ceil(banner.getBoundingClientRect().height)}px`);
    measure();
    const ro = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    ro?.observe(banner);
    return () => {
      ro?.disconnect();
      form.style.removeProperty("--vt-hold-timer-h");
    };
  }, [shown, formRef]);

  return (
    <>
      <div className="vt-hold-sr" role="status" aria-live="polite" aria-atomic="true">
        {view.kind === "hidden" ? "" : view.announcement}
      </div>
      {shown && (
        <div
          ref={bannerRef}
          className={
            "vt-hold-timer" +
            (view.kind === "ended" ? " vt-hold-timer-ended" : view.atCap ? " vt-hold-timer-cap" : "")
          }
          data-hold-timer={view.kind === "ended" ? "ended" : view.atCap ? "cap" : "countdown"}
        >
          <span className="vt-hold-timer-text" aria-hidden="true">
            {view.kind === "countdown" && (
              <>
                <span className="vt-hold-timer-clock">{view.clock}</span>{" "}
              </>
            )}
            <span className="vt-hold-timer-label">{view.label}</span>
          </span>
          {ended && (
            <button type="button" className="vt-link vt-hold-timer-action" data-hold-passive onClick={onBack}>
              Back to your visit
            </button>
          )}
        </div>
      )}
    </>
  );
}
